import { Button, Stack, Typography } from "@mui/material";
import { convertPxToRem, isNativeApp } from "@/utils";
import FullscreenBackground from "../../../../../Common/components/FullscreenBackground";
import { useTranslation } from "react-i18next";
import MaxWidthContainer from "../../../../../Common/components/MaxWidthContainer";
import IconLoader from "../../../../../Common/components/IconLoader";
import Panel from "../../../../../Common/components/Panel";
import { useNavigate } from "react-router-dom";
import {
  trackEvent,
  trackPageViewV2,
} from "../../../../../../services/analytics-adapter";
import { useEffect } from "react";
import {
  FeatureToggleIdentifier,
  isFeatureEnabled,
} from "@/services/feature-toggle-adapter";

interface FaceScanCompleteProps {
  scanResults: any;
  onDone: () => void;
}

interface BottomButtonGroupProps {
  onDone: () => void;
}

function BottomButtonGroup({ onDone }: BottomButtonGroupProps) {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const isTrendedVitalsEnabled = isFeatureEnabled(
    FeatureToggleIdentifier.ENABLE_TRENDED_VITALS
  );

  function onGoHomeButtonClick() {
    trackEvent({
      event: "action.healthCheckCompleteGoHome",
      source: "Health check",
    });
    onDone();
  }

  function onViewHealthProfileClick() {
    trackEvent({
      event: "action.healthCheckCompleteViewHealthProfile",
      source: "Health check",
    });
    return navigate("/home/my-health/health-profile");
  }

  function onViewHealthScoreClick() {
    trackEvent({
      event: "action.healthCheckCompleteViewHealthScore",
      source: "Health check",
    });
    return navigate("/home/health-score");
  }

  return (
    <Stack
      direction={"row"}
      spacing={1}
      p={2}
      bgcolor="background.paper"
      width="100%"
      justifyContent="center"
      textAlign="center"
      alignItems="center"
      left="0"
      bottom="0"
      position="fixed"
      sx={{
        boxShadow:
          "0 4px 100px 0 rgba(40, 44, 80, 0.25), 0 15px 45px 0 rgba(46, 75, 132, 0.27), 0 1.878px 5.635px 0 rgba(46, 75, 132, 0.14)", //TODO: to be added to theme if used more than once
        borderTopLeftRadius: convertPxToRem(10),
        borderTopRightRadius: convertPxToRem(10),
      }}
    >
      <MaxWidthContainer>
        <Stack
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Button
            onClick={onGoHomeButtonClick}
            color="primary"
            variant="outlined"
            fullWidth
          >
            {t("BinahFaceScanSuccess.goHomeButton")}
          </Button>
          {isTrendedVitalsEnabled ? (
            <Button onClick={onViewHealthScoreClick} fullWidth>
              {t("BinahFaceScanSuccess.viewHealthScore")}
            </Button>
          ) : (
            <Button onClick={onViewHealthProfileClick} fullWidth>
              {t("BinahFaceScanSuccess.viewHealthProfile")}
            </Button>
          )}
        </Stack>
      </MaxWidthContainer>
    </Stack>
  );
}

export default function FaceScanComplete({
  scanResults,
  onDone,
}: FaceScanCompleteProps) {
  const { t } = useTranslation();
  const isTrendedVitalsEnabled = isFeatureEnabled(
    FeatureToggleIdentifier.ENABLE_TRENDED_VITALS
  );

  const isBloodPressureAvailable =
    Boolean(scanResults?.bloodPressure) &&
    Boolean(scanResults?.bloodPressure?.value?.systolic) &&
    Boolean(scanResults?.bloodPressure?.value?.diastolic);

  const isHeartRateAvailable =
    Boolean(scanResults?.pulseRate) && Boolean(scanResults?.pulseRate?.value);

  const processedScanResults = [
    {
      value: `${scanResults?.bloodPressure?.value?.systolic}/${scanResults?.bloodPressure?.value?.diastolic}`,
      shouldShow: isBloodPressureAvailable,
      code: "BLOOD_PRESSURE",
    },
    {
      value: scanResults?.pulseRate?.value,
      shouldShow: isHeartRateAvailable,
      code: "PULSE",
    },
  ];

  const shouldShowResults = processedScanResults.some(
    (resultEntry) => resultEntry.shouldShow === true
  );

  useEffect(() => {
    trackPageViewV2({
      pageName: "Health check:Scan results",
      pageSubSection1: "Health check",
      pageSubSection2: "Health check:Scan results",
      pageCategory: "Health check",
    });
  }, []);

  return (
    <Stack color="primary.contrastText" justifyContent="flex-end">
      <Stack
        sx={{
          zIndex: 1,
          position: "relative",
        }}
        bgcolor="primary.main"
        textAlign="center"
        p={8}
        px={6}
        pb={8}
      >
        <IconLoader
          style={{
            alignSelf: "center",
            fontSize: shouldShowResults
              ? convertPxToRem(75)
              : convertPxToRem(135),
          }}
          color="primary"
          icon={"FaceScanInverse"}
        />
        <Typography
          variant="h2"
          color="inherit"
          fontWeight={700}
          fontSize={convertPxToRem(26)}
          mt={4}
          mb={2}
        >
          {t("BinahFaceScanSuccess.title")}
        </Typography>

        {shouldShowResults && (
          <>
            {isTrendedVitalsEnabled ? (
              <Typography
                variant="h3"
                fontWeight={600}
                color="inherit"
                lineHeight={1.5}
              >
                {t("BinahFaceScanSuccess.subtitle")}
              </Typography>
            ) : (
              (
                t<any, any, string[]>("BinahFaceScanSuccess.body", {
                  returnObjects: true,
                }) as []
              ).map((text: string, index: number) => (
                <Typography
                  key={String(text + index)}
                  variant="h3"
                  fontWeight={600}
                  color="inherit"
                  p={2}
                >
                  {text}
                </Typography>
              ))
            )}
          </>
        )}
      </Stack>

      {shouldShowResults && (
        <>
          <Stack
            sx={{
              width: "100%",
              flex: 1,
              display: "flex",
              backgroundColor: "neutral.50",
              position: "relative",
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              p: 2,
              pb: 15,
              zIndex: 2,
              textAlign: "center",
              top: -16,
            }}
          >
            <Typography pb={2} variant="h3" fontWeight={600}>
              {t("BinahFaceScanSuccess.resultsTitle")}
            </Typography>
            {(processedScanResults || []).map((result: any, index: number) => (
              <Stack my={1} key={result.code + index}>
                {result?.shouldShow && (
                  <Panel>
                    <Stack spacing={2} justifyContent="space-between">
                      <Stack direction="row" alignItems="center">
                        <IconLoader
                          style={{
                            alignSelf: "center",
                            fontSize: convertPxToRem(34),
                            marginRight: 2,
                          }}
                          color="primary"
                          icon={"HealthPin"}
                        />
                        <Typography
                          variant="h2"
                          fontWeight={600}
                          color="primary.700"
                        >
                          {result?.value}
                        </Typography>
                      </Stack>
                      <Stack textAlign="left" spacing={1}>
                        <Typography variant="h4" fontWeight={600}>
                          {t(
                            `BinahFaceScanSuccess.results.${result?.code}.label` as any
                          )}
                        </Typography>
                        <Typography variant="body2" color="grey.700">
                          {t(
                            `BinahFaceScanSuccess.results.${result?.code}.description` as any
                          )}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Panel>
                )}
              </Stack>
            ))}
            {isNativeApp() && (
              <Typography variant="body2" color="grey.700" pt={1}>
                {t("BinahScanFlow.instructions.disclaimer.wellness")}
              </Typography>
            )}
            <BottomButtonGroup onDone={onDone} />
          </Stack>

          <FullscreenBackground color="neutral.50" />
        </>
      )}

      {!shouldShowResults && (
        <>
          {(
            t<any, any, string[]>("BinahFaceScanSuccess.noResults.body", {
              returnObjects: true,
            }) as []
          ).map((text: string, index: number) => (
            <Typography
              textAlign="center"
              key={String(text + index)}
              variant="h3"
              color="inherit"
              p={2}
            >
              {text}
            </Typography>
          ))}
          <Stack pb={20}>
            <BottomButtonGroup onDone={onDone} />
          </Stack>
          <FullscreenBackground color="primary.main" />
        </>
      )}
    </Stack>
  );
}
